import { Decimal } from 'decimal.js';
import { z } from 'zod';
import { SalesOrderItemType } from '../../../constants/sales-order-item-type.js';
import { isAdjustmentLineType } from '../../../sales-order-lines/adjustment-line-types.js';
import { TAX_PERCENT_DECIMALS } from '../../../sales-order-totals/tax-percent-decimals.js';
import { nullableUuidString } from '../../zod-helpers/index.js';
import { salesOrderAdjustmentDiscountTypeSchema } from './sales-order-adjustment-discount-type-schema.js';
// Which of amount, percent and discountType apply depends on typeId: Shipping is flat, Tax a percent, Discount a magnitude.
export const salesOrderAdjustmentLineInputSchema = z
    .object({
    typeId: z
        .number()
        .refine((value) => isAdjustmentLineType(value), { message: 'Invalid adjustment line type.' }),
    amount: z.number().max(1_000_000_000, 'Amount must be 1,000,000,000 or less.').optional(),
    percent: z
        .number()
        .min(0, 'Percent cannot be negative.')
        .max(100, 'Percent cannot exceed 100.')
        .optional(),
    discountType: salesOrderAdjustmentDiscountTypeSchema.optional(),
    // TAX only: the accounting.tax_rate this line applies; null or absent is a manual tax.
    taxRateId: nullableUuidString('Invalid tax rate selection.').optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.taxRateId != null && value.typeId !== SalesOrderItemType.TAX) {
        context.addIssue({
            code: 'custom',
            message: 'Only tax adjustments take a tax rate.',
            path: ['taxRateId'],
        });
    }
    if (value.typeId === SalesOrderItemType.SHIPPING) {
        if (value.amount === undefined || value.amount < 0) {
            context.addIssue({
                code: 'custom',
                message: 'Shipping amount is required and cannot be negative.',
                path: ['amount'],
            });
        }
    }
    else if (value.typeId === SalesOrderItemType.TAX) {
        if (value.percent === undefined) {
            context.addIssue({
                code: 'custom',
                message: 'Tax percent is required.',
                path: ['percent'],
            });
        }
        else if (value.percent <= 0) {
            context.addIssue({
                code: 'custom',
                message: 'Tax percent must be greater than 0.',
                path: ['percent'],
            });
        }
        else if (!new Decimal(value.percent).equals(new Decimal(value.percent).toDecimalPlaces(TAX_PERCENT_DECIMALS))) {
            context.addIssue({
                code: 'custom',
                message: `Tax percent can have at most ${TAX_PERCENT_DECIMALS} decimal places.`,
                path: ['percent'],
            });
        }
    }
    else if (value.typeId === SalesOrderItemType.DISCOUNT) {
        if (value.amount === undefined || value.amount < 0) {
            context.addIssue({
                code: 'custom',
                message: 'Discount amount is required and cannot be negative.',
                path: ['amount'],
            });
        }
        if (value.discountType === undefined) {
            context.addIssue({
                code: 'custom',
                message: 'Discount type (flat or percentage) is required.',
                path: ['discountType'],
            });
        }
        if (value.discountType === 'percentage' && value.amount !== undefined && value.amount > 100) {
            context.addIssue({
                code: 'custom',
                message: 'Discount percentage cannot exceed 100.',
                path: ['amount'],
            });
        }
    }
});
//# sourceMappingURL=sales-order-adjustment-line-input-schema.js.map